import { getDomains } from '@/helpers/getDomains';
import { logger } from '@/utils/logger/logger';
import type { components } from '@heidi/schema/ml-scribe';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { completeCibaLogin } from './useCibaCompleteLogin';

type CibaStatus =
  | 'idle'
  | 'initiating'
  | 'pending'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'error';

type InitiateCibaResponse = components['schemas']['InitiateCibaResponse'];

interface UseCibaLoginReturn {
  status: CibaStatus;
  matchCode: string | null;
  expiresAtMs: number | null;
  initiate: (email: string) => Promise<void>;
  cancel: () => void;
}

const POLL_INTERVAL_MS = 5000;
const SLOW_DOWN_INCREMENT_MS = 5000;
const FALLBACK_MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes

function authReqIdSuffix(authReqId: string): string {
  return authReqId.slice(-6);
}

function errorContext(error: unknown): Record<string, unknown> {
  const responseStatus = (error as { response?: { status?: unknown } })?.response?.status;
  const context: Record<string, unknown> = {
    errorType: (error as Error)?.constructor?.name,
    message: error instanceof Error ? error.message : String(error),
  };
  if (responseStatus !== undefined) {
    context.responseStatus = responseStatus;
  }
  return context;
}

function expiresInToMs(expiresIn: number): number {
  if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
    return FALLBACK_MAX_POLL_DURATION_MS;
  }
  return expiresIn * 1000;
}

function intervalToMs(interval: number | undefined): number {
  if (interval === undefined || !Number.isFinite(interval) || interval <= 0) {
    return POLL_INTERVAL_MS;
  }
  return interval * 1000;
}

function completionResultToStatus(
  result: Awaited<ReturnType<typeof completeCibaLogin>>
): CibaStatus {
  if (result === 'completed') return 'approved';
  if (result === 'failed') return 'error';
  if (result === 'slow_down') return 'pending';
  return result;
}

function terminalStatusEvent(status: CibaStatus): string | null {
  switch (status) {
    case 'approved':
      return 'ciba_web_approved';
    case 'denied':
      return 'ciba_web_denied';
    case 'expired':
      return 'ciba_web_expired';
    case 'error':
      return 'ciba_web_error';
    default:
      return null;
  }
}

export function useCibaLogin(): UseCibaLoginReturn {
  const [status, setStatus] = useState<CibaStatus>('idle');
  const [matchCode, setMatchCode] = useState<string | null>(null);
  const [expiresAtMs, setExpiresAtMs] = useState<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);
  const pollErrorLoggedRef = useRef(false);
  const activeAuthReqIdRef = useRef<string | null>(null);
  const requestGenerationRef = useRef(0);

  const isCurrentRequest = useCallback((reqId: string, requestGeneration: number) => {
    return (
      activeAuthReqIdRef.current === reqId &&
      requestGenerationRef.current === requestGeneration
    );
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const clearSessionState = useCallback(() => {
    setMatchCode(null);
    setExpiresAtMs(null);
  }, []);

  const cancel = useCallback(() => {
    const authReqId = activeAuthReqIdRef.current;
    if (authReqId) {
      logger.info({
        event: 'ciba_web_cancelled',
        authReqIdSuffix: authReqIdSuffix(authReqId),
      });
    }
    requestGenerationRef.current += 1;
    activeAuthReqIdRef.current = null;
    stopPolling();
    clearSessionState();
    setStatus('idle');
  }, [clearSessionState, stopPolling]);

  useEffect(() => cancel, [cancel]);

  const startPolling = useCallback(
    (
      reqId: string,
      completionToken: string,
      pollIntervalMs: number,
      maxPollDurationMs: number,
      requestGeneration: number
    ) => {
      let currentPollIntervalMs = pollIntervalMs;
      let isPollInFlight = false;
      let isTerminal = false;
      pollStartRef.current = Date.now();
      pollErrorLoggedRef.current = false;
      const poll = async () => {
        if (isPollInFlight || isTerminal || !isCurrentRequest(reqId, requestGeneration)) {
          return;
        }

        const elapsedMs = Date.now() - pollStartRef.current;
        if (elapsedMs > maxPollDurationMs) {
          logger.warn({
            event: 'ciba_web_poll_timeout',
            authReqIdSuffix: authReqIdSuffix(reqId),
            elapsedMs,
            maxPollDurationMs,
          });
          stopPolling();
          isTerminal = true;
          logger.info({
            event: 'ciba_web_expired',
            authReqIdSuffix: authReqIdSuffix(reqId),
          });
          setStatus('expired');
          return;
        }

        isPollInFlight = true;
        try {
          const result = await completeCibaLogin({
            authReqId: reqId,
            completionToken,
          });

          if (!isCurrentRequest(reqId, requestGeneration)) return;

          if (result === 'slow_down') {
            currentPollIntervalMs += SLOW_DOWN_INCREMENT_MS;
            logger.info({
              event: 'ciba_web_poll_slow_down',
              authReqIdSuffix: authReqIdSuffix(reqId),
              elapsedMs,
              pollIntervalMs: currentPollIntervalMs,
            });
            stopPolling();
            pollIntervalRef.current = setInterval(
              () => void poll(),
              currentPollIntervalMs
            );
            return;
          }

          if (result !== 'pending') {
            const nextStatus = completionResultToStatus(result);
            logger.info({
              event: 'ciba_web_poll_terminal_status',
              authReqIdSuffix: authReqIdSuffix(reqId),
              status: result,
              elapsedMs,
            });
            const lifecycleEvent = terminalStatusEvent(nextStatus);
            if (lifecycleEvent) {
              logger.info({
                event: lifecycleEvent,
                authReqIdSuffix: authReqIdSuffix(reqId),
              });
            }
            stopPolling();
            isTerminal = true;
            setStatus(nextStatus);
          }
        } catch (error) {
          if (!isCurrentRequest(reqId, requestGeneration)) return;

          if (!pollErrorLoggedRef.current) {
            pollErrorLoggedRef.current = true;
            logger.warn({
              event: 'ciba_web_poll_transient_failure',
              authReqIdSuffix: authReqIdSuffix(reqId),
              ...errorContext(error),
            });
          }
          // transient network error — keep polling
        } finally {
          isPollInFlight = false;
        }
      };
      pollIntervalRef.current = setInterval(() => void poll(), pollIntervalMs);
    },
    [isCurrentRequest, stopPolling]
  );

  const initiate = useCallback(
    async (email: string) => {
      const requestGeneration = requestGenerationRef.current + 1;
      requestGenerationRef.current = requestGeneration;
      activeAuthReqIdRef.current = null;
      clearSessionState();
      setStatus('initiating');
      stopPolling();

      try {
        logger.info({ event: 'ciba_web_initiate_started' });
        const { baseV1IdentityBrokerUrl } = getDomains();
        const { data } = await axios.post<InitiateCibaResponse>(
          `${baseV1IdentityBrokerUrl}ciba/initiate`,
          { email }
        );

        if (requestGenerationRef.current !== requestGeneration) return;

        const maxPollDurationMs = expiresInToMs(data.expires_in);
        const pollIntervalMs = intervalToMs(data.interval);
        logger.info({
          event: 'ciba_web_initiate_succeeded',
          authReqIdSuffix: authReqIdSuffix(data.auth_req_id),
          expiresIn: data.expires_in,
          pollIntervalMs,
          maxPollDurationMs,
        });
        activeAuthReqIdRef.current = data.auth_req_id;
        setMatchCode(data.match_code);
        setExpiresAtMs(Date.now() + maxPollDurationMs);
        setStatus('pending');
        logger.info({
          event: 'ciba_web_waiting_viewed',
          authReqIdSuffix: authReqIdSuffix(data.auth_req_id),
        });
        startPolling(
          data.auth_req_id,
          data.completion_token,
          pollIntervalMs,
          maxPollDurationMs,
          requestGeneration
        );
      } catch (error) {
        if (requestGenerationRef.current !== requestGeneration) return;

        logger.warn({
          event: 'ciba_web_initiate_failed',
          ...errorContext(error),
        });
        logger.info({ event: 'ciba_web_error' });
        setStatus('error');
      }
    },
    [clearSessionState, stopPolling, startPolling]
  );

  return { status, matchCode, expiresAtMs, initiate, cancel };
}
